/* ==========================================================================
   WAYPOINT — shared-axis screen transition + per-path scroll restoration.
   Wrap each route's rendered content in <Screen path={pathname}>.

   ENTER-ONLY, and deliberately so: there is no `exit` variant here and no
   AnimatePresence around this component in App.tsx. An exit animation needs a
   presence parent to remove the node when the animation ends, and that removal
   handshake is what stranded the whole app on a blank page (App.tsx documents
   the exact framer-motion code path). Only one screen is ever mounted, so the
   outgoing one simply goes; the incoming one slides and fades in over the same
   ground it would have. Do not add `exit` back without re-reading that comment.
   ========================================================================== */

import { motion, useReducedMotion } from 'framer-motion';
import { useEffect, useRef, type ReactNode } from 'react';
import { tabIndexForPath } from './BottomNav';

const scrollPositions = new Map<string, number>();
let lastPath: string | null = null;

const SPRING = { type: 'spring' as const, stiffness: 420, damping: 38, mass: 0.9 };

/** Position springs; opacity does NOT.
 *
 *  A spring driving opacity has no definite end, and when a fast tab tap interrupts the enter
 *  animation mid-flight the spring re-settles against the incoming velocity and stops short —
 *  measurably at 0.833 — leaving the screen permanently dimmed until the next navigation. A
 *  tween has a fixed duration and a fixed end value, so an interrupted fade always lands on 1.
 *  (Predates the Log; reproduced on Trips<->Scooty and Trips<->Map too.) */
const SCREEN_TRANSITION = { x: SPRING, opacity: { duration: 0.18, ease: 'easeOut' as const } };

/** How long to let the enter animation run before assuming it never will. Comfortably past the
 *  0.18s fade and the spring's settle, short enough that a stalled screen rights itself within a
 *  blink of the tab coming back to the foreground. */
const ENTER_SETTLE_MS = 700;

const TRIP_DETAIL_RE = /^\/trips\/[^/]+$/;

/** Transition direction from the visible bottom-nav tab order (not a
 * lexicographic path compare — '/journal' < '/map' alphabetically but sits
 * to its right in the nav). Pushing into trip detail always slides forward;
 * returning from it always slides back. */
function directionFor(path: string, prevPath: string | null): 1 | -1 {
  if (prevPath === null) return 1;
  const isDetail = TRIP_DETAIL_RE.test(path);
  const wasDetail = TRIP_DETAIL_RE.test(prevPath);
  if (isDetail && !wasDetail) return 1;
  if (!isDetail && wasDetail) return -1;
  const from = tabIndexForPath(prevPath);
  const to = tabIndexForPath(path);
  if (from === -1 || to === -1) return 1;
  return to >= from ? 1 : -1;
}

interface ScreenProps {
  path: string;
  children: ReactNode;
}

export function Screen({ path, children }: ScreenProps) {
  const reduceMotion = useReducedMotion();
  const direction = directionFor(path, lastPath);
  const screenRef = useRef<HTMLDivElement>(null);
  const entered = useRef(false);

  // Belt to the enter-only braces above: a screen's visibility must not depend on an animation
  // actually running. framer animates off animation frames, and the browser hands those out at
  // its own discretion — a backgrounded tab, a locked phone, a foregrounded tab switcher all
  // stop them cold. The element is then left at `initial` (opacity 0) with real content inside
  // it: the screen reads as blank and stays that way. So if the enter hasn't reported complete
  // by the deadline, put it in its final state by hand. Timers are throttled in a hidden tab,
  // not cancelled, so a screen stranded there rights itself when the user comes back.
  useEffect(() => {
    entered.current = false;
    const timer = window.setTimeout(() => {
      if (entered.current || !screenRef.current) return;
      screenRef.current.style.opacity = '1';
      screenRef.current.style.transform = 'none';
    }, ENTER_SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [path]);

  useEffect(() => {
    // Save the outgoing screen's scroll position, restore the incoming one.
    return () => {
      scrollPositions.set(path, window.scrollY);
    };
  }, [path]);

  useEffect(() => {
    const saved = scrollPositions.get(path) ?? 0;
    lastPath = path;
    if (saved === 0) {
      window.scrollTo(0, 0);
      return;
    }
    // A screen typically mounts with only its skeleton in the DOM (its data
    // fetch is still in flight), which is shorter than the real content —
    // restoring scroll synchronously here gets clamped to that short
    // height and lands near the top. Re-issue the restore every time the
    // page's height changes (skeleton -> real content, images loading in,
    // etc.) until the page is tall enough to actually hold the saved
    // position, or a couple of seconds pass with nothing settling.
    let settled = false;
    const attemptRestore = () => {
      window.scrollTo(0, saved);
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      if (maxScroll >= saved - 1) settled = true;
    };
    attemptRestore();
    const ro = new ResizeObserver(() => {
      if (!settled) attemptRestore();
    });
    ro.observe(document.body);
    const timeout = window.setTimeout(() => ro.disconnect(), 2000);
    return () => {
      ro.disconnect();
      window.clearTimeout(timeout);
    };
  }, [path]);

  if (reduceMotion) {
    return (
      <motion.div
        ref={screenRef}
        className="screen"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.12 }}
        onAnimationComplete={() => {
          entered.current = true;
        }}
      >
        {children}
      </motion.div>
    );
  }

  return (
    <motion.div
      ref={screenRef}
      className="screen"
      initial={{ opacity: 0, x: 24 * direction }}
      animate={{ opacity: 1, x: 0 }}
      transition={SCREEN_TRANSITION}
      onAnimationComplete={() => {
        entered.current = true;
      }}
    >
      {children}
    </motion.div>
  );
}
